<template>
  <div class="grid grid-cols-2 gap-4 p-4 bg-gray-50">
      <!-- Task Types List -->
      <div class="bg-white rounded-lg border border-gray-200">
        <div class="flex items-center gap-3 px-3.5 py-2.5 border-b border-gray-100">
          <span style="font-size: 13px;" class="font-semibold text-gray-900">Task types</span>
          <span style="font-size: 11px;" class="text-gray-500">{{ taskTypes.length }} configured</span>
          <span class="flex-1" />
          <button @click="showTaskTypeModal = true" style="font-size: 12px;" class="text-gray-700 hover:text-gray-900 font-medium flex items-center gap-1">
            <font-awesome-icon :icon="['fas', 'plus']" style="font-size: 10px;" /> New type
          </button>
        </div>

        <div>
          <div v-if="taskTypes.length === 0" class="py-8 text-center text-gray-400" style="font-size: 12px;">No task types</div>
          <div
            v-for="(tt, index) in taskTypes"
            :key="tt.id"
            @click="selectTaskType(tt)"
            class="group flex items-center gap-2.5 px-3.5 py-2.5 cursor-pointer"
            :class="[
              selectedTaskType?.id === tt.id ? 'bg-gray-50' : 'hover:bg-gray-50',
              index < taskTypes.length - 1 ? 'border-b border-gray-100' : ''
            ]"
          >
            <div class="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
              :style="{ backgroundColor: tt.color || '#3B82F6' }"
            >
              <font-awesome-icon :icon="['fas', tt.icon || 'tasks']" class="text-white" style="font-size: 9px;" />
            </div>
            <span style="font-size: 13px;" class="font-medium text-gray-900 flex-1 truncate">{{ tt.name }}</span>
            <span
              v-if="wiredTaskTypeIds.has(tt.id)"
              style="font-size: 11px;"
              class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-50 text-green-700"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-green-500"></span>
              wired
            </span>
            <span
              v-if="sharedMetricsFor(tt.id).length > 1"
              style="font-size: 11px;"
              class="shared-badge inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 relative cursor-default"
            >
              <font-awesome-icon :icon="['fas', 'layer-group']" style="font-size: 8px;" />
              {{ sharedMetricsFor(tt.id).length }} programs
              <span class="shared-tooltip">
                <span class="font-semibold">Shared across:</span>
                <span v-for="name in sharedMetricsFor(tt.id)" :key="name" class="block">· {{ name }}</span>
                <span class="block mt-1 text-amber-500">Editing this task type or its actions changes all these programs.</span>
              </span>
            </span>
            <span style="font-size: 11px;" class="text-gray-400">{{ getActionCount(tt) }} actions</span>
            <div class="flex items-center gap-1">
              <button @click.stop="editTaskType(tt)" class="p-1 text-gray-400 hover:text-blue-600 rounded" title="Rename / edit task type">
                <font-awesome-icon :icon="['fas', 'pen']" style="font-size: 11px;" />
              </button>
              <button @click.stop="deleteTaskType(tt)" class="p-1 text-gray-400 hover:text-red-600 rounded opacity-0 group-hover:opacity-100" title="Delete task type">
                <font-awesome-icon :icon="['fas', 'trash']" style="font-size: 11px;" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Detail Panel -->
      <div v-if="!selectedTaskType" class="bg-white rounded-lg border border-gray-200 flex items-center justify-center text-gray-400" style="font-size: 13px; min-height: 200px;">
        Select a task type to configure
      </div>

      <div v-else class="bg-white rounded-lg border border-gray-200">
        <div class="flex items-center gap-3 px-3.5 py-2.5 border-b border-gray-100">
          <span style="font-size: 13px;" class="font-semibold text-gray-900">{{ selectedTaskType.name }}</span>
          <span style="font-size: 11px;" class="text-gray-500">Edit task type configuration</span>
          <span class="flex-1" />
          <button @click="showActionModal = true" style="font-size: 12px;" class="text-gray-700 hover:text-gray-900 font-medium flex items-center gap-1">
            <font-awesome-icon :icon="['fas', 'plus']" style="font-size: 10px;" /> Add action
          </button>
        </div>

        <div class="p-4 space-y-4">
          <!-- Actions List -->
          <div>
            <div class="flex items-center gap-2 mb-2">
              <span style="font-size: 11px;" class="font-medium text-gray-500 uppercase tracking-wide">Actions</span>
              <label class="flex items-center gap-1 text-gray-500 ml-auto" style="font-size: 11px;">
                <input v-model="showInactiveActions" type="checkbox" class="h-3 w-3 rounded border-gray-300 accent-gray-900">
                Show inactive
              </label>
            </div>

            <div v-if="loadingActions" class="py-4 text-center text-gray-400" style="font-size: 12px;">Loading...</div>
            <div v-else-if="filteredTaskActions.length === 0" class="py-4 text-center text-gray-400" style="font-size: 12px;">No actions</div>
            <div v-else class="border border-gray-100 rounded-md">
              <div
                v-for="(action, index) in filteredTaskActions"
                :key="action.id"
                class="group flex items-center gap-2.5 px-3 py-2"
                :class="[
                  { 'opacity-50': !action.active },
                  index < filteredTaskActions.length - 1 ? 'border-b border-gray-100' : ''
                ]"
              >
                <span class="text-gray-300">
                  <font-awesome-icon :icon="['fas', 'grip-vertical']" style="font-size: 11px;" />
                </span>
                <span class="font-mono text-gray-500" style="font-size: 11px;">{{ index + 1 }}</span>
                <span class="flex-1" style="font-size: 13px;" :class="action.active ? 'text-gray-800' : 'text-gray-500'">{{ action.name }}</span>
                <span
                  v-if="action.instruction_name"
                  style="font-size: 11px;"
                  class="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
                >{{ action.instruction_name }}</span>
                <div class="flex items-center gap-1 opacity-60 group-hover:opacity-100">
                  <button @click="editAction(action)" class="p-1 text-gray-400 hover:text-blue-600" title="Edit action">
                    <font-awesome-icon :icon="['fas', 'pen']" style="font-size: 10px;" />
                  </button>
                  <button @click="toggleActionStatus(action)" class="p-1" :class="action.active ? 'text-gray-400 hover:text-orange-600' : 'text-gray-400 hover:text-green-600'" :title="action.active ? 'Deactivate' : 'Activate'">
                    <font-awesome-icon :icon="['fas', action.active ? 'ban' : 'check-circle']" style="font-size: 10px;" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Work Types -->
          <div>
            <div class="flex items-center gap-2 mb-2">
              <span style="font-size: 11px;" class="font-medium text-gray-500 uppercase tracking-wide">Work Types</span>
              <button @click="showWorkTypeAssignModal = true" style="font-size: 11px;" class="text-gray-500 hover:text-gray-700 font-medium flex items-center gap-1 ml-auto">
                <font-awesome-icon :icon="['fas', 'plus']" style="font-size: 9px;" /> Assign
              </button>
            </div>

            <div v-if="!selectedTaskType.work_types || selectedTaskType.work_types.length === 0" class="py-3 text-center text-gray-400 border border-gray-100 rounded-md" style="font-size: 12px;">No work types assigned</div>
            <div v-else class="border border-gray-100 rounded-md">
              <div
                v-for="(wt, index) in selectedTaskType.work_types"
                :key="wt.id"
                class="group flex items-center gap-2.5 px-3 py-2"
                :class="index < selectedTaskType.work_types.length - 1 ? 'border-b border-gray-100' : ''"
              >
                <span class="flex-1 font-medium text-gray-900" style="font-size: 13px;">{{ wt.display_name || wt.name }}</span>
                <span class="font-mono text-gray-500" style="font-size: 11px;">{{ wt.name }}</span>
                <button @click="removeWorkType(wt)" class="p-1 text-gray-300 hover:text-red-600 hidden group-hover:block">
                  <font-awesome-icon :icon="['fas', 'times']" style="font-size: 10px;" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    <!-- Task Type Modal -->
    <transition name="modal">
      <div v-if="showTaskTypeModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-sm w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">{{ editingTaskType ? 'Edit' : 'Create' }} Task Type</h3>
          </div>
          <div class="p-4 space-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Name</label>
              <input v-model="taskTypeForm.name" type="text" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500" placeholder="Task type name">
            </div>
            <div class="flex gap-3">
              <div class="flex-1">
                <label class="block text-xs font-medium text-gray-700 mb-1">Color</label>
                <div class="flex items-center gap-2">
                  <input v-model="taskTypeForm.color" type="color" class="h-8 w-12 border border-gray-300 rounded cursor-pointer">
                  <input v-model="taskTypeForm.color" type="text" class="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500" placeholder="#3B82F6">
                </div>
              </div>
              <div class="w-28">
                <label class="block text-xs font-medium text-gray-700 mb-1">Icon</label>
                <input v-model="taskTypeForm.icon" type="text" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500" placeholder="tasks">
              </div>
            </div>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="closeTaskTypeModal" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="saveTaskType" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700">{{ editingTaskType ? 'Update' : 'Create' }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Action Modal -->
    <transition name="modal">
      <div v-if="showActionModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">{{ editingAction ? 'Edit' : 'Create' }} Action</h3>
          </div>
          <div class="p-4 space-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Name</label>
              <input v-model="actionForm.name" type="text" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500" placeholder="Action name">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Instruction Type</label>
              <select v-model="actionForm.task_instruction_type" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                <option :value="null">-- Select --</option>
                <option v-for="inst in instructionTypes" :key="inst.id" :value="inst.id">{{ inst.name }}</option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Config (JSON)</label>
              <div v-if="configHint" class="mb-1 p-2 bg-blue-50 border border-blue-200 rounded">
                <p class="text-[10px] text-blue-800 font-medium mb-0.5">Required config for this instruction type:</p>
                <pre class="text-[10px] text-blue-700 font-mono whitespace-pre-wrap">{{ configHint }}</pre>
              </div>
              <textarea v-model="actionForm.config_json" rows="5" class="w-full px-2 py-1 text-xs border border-gray-300 rounded font-mono" placeholder="{}"></textarea>
              <p v-if="configJsonError" class="text-[10px] text-red-500 mt-0.5">{{ configJsonError }}</p>
            </div>

            <label class="flex items-center gap-1.5 text-xs text-gray-700">
              <input v-model="actionForm.active" type="checkbox" class="h-3 w-3 rounded border-gray-300 text-blue-600">
              Active
            </label>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="closeActionModal" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="saveAction" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700">{{ editingAction ? 'Update' : 'Create' }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Confirm Modal (generic + shared) -->
    <transition name="modal">
      <div v-if="confirmModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-sm w-full">
          <div class="px-4 py-3 border-b rounded-t-lg flex items-center gap-2"
            :class="confirmModal.variant === 'danger' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'"
          >
            <div class="w-6 h-6 rounded-full flex items-center justify-center"
              :class="confirmModal.variant === 'danger' ? 'bg-red-100' : 'bg-amber-100'"
            >
              <font-awesome-icon
                :icon="['fas', confirmModal.variant === 'danger' ? 'trash' : 'triangle-exclamation']"
                :class="confirmModal.variant === 'danger' ? 'text-red-600' : 'text-amber-600'"
                style="font-size: 11px;"
              />
            </div>
            <h3 class="text-sm font-semibold" :class="confirmModal.variant === 'danger' ? 'text-red-900' : 'text-amber-900'">{{ confirmModal.title }}</h3>
          </div>
          <div class="px-4 py-4">
            <p class="text-sm text-gray-700" :class="{ 'mb-3': confirmModal.programs }">{{ confirmModal.message }}</p>
            <div v-if="confirmModal.programs" class="bg-amber-50 border border-amber-100 rounded px-3 py-2">
              <p style="font-size: 11px;" class="font-medium text-amber-800 mb-1">Affected programs:</p>
              <ul class="space-y-0.5">
                <li v-for="name in confirmModal.programs" :key="name" style="font-size: 12px;" class="text-amber-700 flex items-center gap-1.5">
                  <span class="w-1 h-1 rounded-full bg-amber-400"></span>
                  {{ name }}
                </li>
              </ul>
            </div>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="resolveConfirmModal(false)" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="resolveConfirmModal(true)" class="px-3 py-1.5 text-xs text-white rounded"
              :class="confirmModal.variant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'"
            >{{ confirmModal.confirmLabel || 'Continue' }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Toast Notification -->
    <transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <div v-if="toast" class="fixed bottom-4 right-4 z-50 max-w-sm">
        <div class="flex items-start gap-2 px-4 py-3 rounded-lg shadow-lg border"
          :class="toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'"
        >
          <font-awesome-icon :icon="['fas', toast.type === 'error' ? 'circle-exclamation' : 'check-circle']" class="mt-0.5" style="font-size: 13px;" />
          <div class="flex-1">
            <p class="text-xs font-medium">{{ toast.title }}</p>
            <p v-if="toast.detail" class="text-xs mt-0.5 opacity-80">{{ toast.detail }}</p>
          </div>
          <button @click="toast = null" class="text-gray-400 hover:text-gray-600">
            <font-awesome-icon :icon="['fas', 'times']" style="font-size: 10px;" />
          </button>
        </div>
      </div>
    </transition>

    <!-- Work Type Assignment Modal -->
    <transition name="modal">
      <div v-if="showWorkTypeAssignModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-sm w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">Assign Work Types to {{ selectedTaskType?.name }}</h3>
          </div>
          <div class="p-4">
            <div v-if="loadingWorkTypes" class="py-4 text-center text-xs text-gray-400">Loading...</div>
            <div v-else-if="availableWorkTypes.length === 0" class="py-4 text-center text-xs text-gray-400">No work types available</div>
            <div v-else class="max-h-64 overflow-y-auto space-y-0.5">
              <label
                v-for="wt in availableWorkTypes"
                :key="wt.id"
                class="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer text-xs"
              >
                <input type="checkbox" :value="wt.id" v-model="selectedWorkTypeIds" class="h-3 w-3 rounded border-gray-300 text-blue-600">
                <span class="text-gray-900">{{ wt.display_name || wt.name }}</span>
                <span class="text-[10px] text-gray-400 ml-auto">{{ wt.id }}</span>
              </label>
            </div>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-between items-center">
            <span class="text-[10px] text-gray-500">{{ selectedWorkTypeIds.length }} selected</span>
            <div class="flex gap-2">
              <button @click="closeWorkTypeAssignModal" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
              <button @click="assignWorkTypes" :disabled="selectedWorkTypeIds.length === 0" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Assign</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  components: { FontAwesomeIcon },
  data() {
    return {
      taskTypes: [],
      taskActions: [],
      actionCounts: {},
      wiredTaskTypeIds: new Set(),
      metrics: [],
      taskTypeMetricIds: {},
      instructionTypes: [],
      allWorkTypes: [],
      selectedTaskType: null,
      loadingActions: false,
      loadingWorkTypes: false,
      showInactiveActions: false,
      showTaskTypeModal: false,
      editingTaskType: null,
      taskTypeForm: { name: '', color: '#3B82F6', icon: 'tasks' },
      showActionModal: false,
      editingAction: null,
      actionForm: { name: '', description: '', task_instruction_type: null, active: true, config_json: '{}' },
      showWorkTypeAssignModal: false,
      selectedWorkTypeIds: [],
      confirmModal: null,
      toast: null,
      toastTimer: null,
    };
  },
  watch: {
    'actionForm.task_instruction_type'(newType) {
      // Auto-fill config template when instruction type changes and config is empty/default
      if (!newType || this.editingAction) return;
      const current = this.actionForm.config_json?.trim();
      if (current && current !== '{}') return;
      if (this.configHint) {
        this.actionForm.config_json = this.configHint;
      }
    },
  },
  computed: {
    availableWorkTypes() {
      if (!this.selectedTaskType || !this.allWorkTypes) return [];
      const assignedIds = (this.selectedTaskType.work_types || []).map(wt => wt.id);
      return this.allWorkTypes.filter(wt => !assignedIds.includes(wt.id));
    },
    filteredTaskActions() {
      return this.showInactiveActions ? this.taskActions : this.taskActions.filter(a => a.active !== false);
    },
    configJsonError() {
      if (!this.actionForm.config_json || this.actionForm.config_json.trim() === '') return null;
      try { JSON.parse(this.actionForm.config_json); return null; }
      catch (e) { return 'Invalid JSON'; }
    },
    configHint() {
      const hints = {
        1: '{\n  "default_due_days": 7,\n  "date_picker": {\n    "enabled": true,\n    "target": "due_date",\n    "label": "Next call due date",\n    "required": false,\n    "default_days": 7\n  }\n}',
        6: '{\n  "default_snooze_days": 1,\n  "date_picker": {\n    "enabled": true,\n    "target": "snooze_until",\n    "label": "Snooze until",\n    "required": false\n  }\n}',
        9: '{\n  "max_repeats": null,\n  "repeat_strategy": {\n    "type": "default",\n    "interval_days": 7\n  }\n}',
        10: '{\n  "target_metric_slug": "METRIC_SLUG",\n  "close_current": true\n}',
        11: '{\n  "skip_count": 2,\n  "default_due_days": 7\n}',
        15: '{\n  "rediscovery_days": 30\n}',
        16: '{\n  "default_push_days": 2\n}',
      };
      return hints[this.actionForm.task_instruction_type] || null;
    },
  },
  methods: {
    getActionCount(tt) {
      if (this.selectedTaskType?.id === tt.id) return this.taskActions.length;
      return this.actionCounts[tt.id] || 0;
    },
    configSummary(config) {
      if (!config || Object.keys(config).length === 0) return '—';
      return Object.entries(config)
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
        .join(', ');
    },

    async fetchTaskTypes() {
      try {
        const { data } = await axios.get('/api/tasktypes/');
        this.taskTypes = data;
        this.fetchActionCounts();
      } catch (e) { console.error(e); }
    },
    async fetchActionCounts() {
      const counts = {};
      await Promise.all(this.taskTypes.map(async (tt) => {
        try {
          const { data } = await axios.get('/api/taskactiontypes/', { params: { task_type: tt.id } });
          counts[tt.id] = data.length;
        } catch { counts[tt.id] = 0; }
      }));
      this.actionCounts = counts;
    },
    async fetchInstructionTypes() {
      try {
        const { data } = await axios.get('/api/taskinstructiontypes/');
        this.instructionTypes = data;
      } catch (e) { console.error(e); }
    },
    async fetchMetrics() {
      try {
        const { data } = await axios.get('/api/metrics/');
        this.metrics = data;
      } catch (e) { console.error(e); }
    },
    async fetchMetricTaskTypes() {
      try {
        const { data } = await axios.get('/api/metrictasktypes/');
        this.wiredTaskTypeIds = new Set(data.map(mtt => mtt.task_type));
        const shared = {};
        data.forEach(mtt => {
          (shared[mtt.task_type] = shared[mtt.task_type] || new Set()).add(mtt.metric);
        });
        this.taskTypeMetricIds = shared;
      } catch (e) { console.error(e); }
    },
    showConfirm({ title, message, programs, variant, confirmLabel }) {
      return new Promise(resolve => {
        this.confirmModal = { title, message, programs, variant: variant || 'warning', confirmLabel, resolve };
      });
    },
    resolveConfirmModal(result) {
      if (this.confirmModal?.resolve) this.confirmModal.resolve(result);
      this.confirmModal = null;
    },
    showToast(title, detail, type = 'error') {
      clearTimeout(this.toastTimer);
      this.toast = { title, detail, type };
      this.toastTimer = setTimeout(() => { this.toast = null; }, 5000);
    },
    sharedMetricsFor(taskTypeId) {
      const ids = this.taskTypeMetricIds[taskTypeId];
      if (!ids || ids.size === 0) return [];
      const names = [];
      ids.forEach(mid => {
        const m = this.metrics.find(x => x.id === mid);
        names.push(m ? m.name : ('Metric ' + mid));
      });
      return names;
    },
    async fetchAllWorkTypes() {
      try {
        const { data } = await axios.get('/api/worktypes/');
        this.allWorkTypes = data;
      } catch (e) { console.error(e); }
    },
    async selectTaskType(taskType) {
      this.selectedTaskType = taskType;
      this.loadingActions = true;
      try {
        const { data } = await axios.get('/api/taskactiontypes/', { params: { task_type: taskType.id } });
        this.taskActions = data;
        this.actionCounts[taskType.id] = data.length;
      } catch (e) { console.error(e); }
      finally { this.loadingActions = false; }
    },

    // Task Type CRUD
    editTaskType(tt) {
      this.editingTaskType = tt;
      this.taskTypeForm = { name: tt.name, color: tt.color, icon: tt.icon };
      this.showTaskTypeModal = true;
    },
    async saveTaskType() {
      try {
        if (this.editingTaskType) {
          const shared = this.sharedMetricsFor(this.editingTaskType.id);
          if (shared.length > 1) {
            const ok = await this.showConfirm({
              title: 'Shared task type',
              message: `"${this.editingTaskType.name}" is shared across ${shared.length} programs. Editing it will change all of them.`,
              programs: shared,
            });
            if (!ok) return;
          }
          await axios.patch(`/api/tasktypes/${this.editingTaskType.id}/`, this.taskTypeForm);
        } else {
          await axios.post('/api/tasktypes/', this.taskTypeForm);
        }
        this.closeTaskTypeModal();
        await this.fetchTaskTypes();
        if (this.editingTaskType && this.selectedTaskType?.id === this.editingTaskType.id) {
          this.selectedTaskType = this.taskTypes.find(tt => tt.id === this.editingTaskType.id);
        }
      } catch (e) { console.error(e); this.showToast('Error saving task type', e.response?.data?.detail); }
    },
    async deleteTaskType(tt) {
      const shared = this.sharedMetricsFor(tt.id);
      const ok = await this.showConfirm(shared.length > 1
        ? { title: 'Shared task type', message: `"${tt.name}" is shared across ${shared.length} programs. Deleting it will affect all of them.`, programs: shared, variant: 'danger', confirmLabel: 'Delete' }
        : { title: 'Delete task type', message: `Are you sure you want to delete "${tt.name}"?`, variant: 'danger', confirmLabel: 'Delete' }
      );
      if (!ok) return;
      try {
        await axios.delete(`/api/tasktypes/${tt.id}/`);
        this.fetchTaskTypes();
        if (this.selectedTaskType?.id === tt.id) { this.selectedTaskType = null; this.taskActions = []; }
      } catch (e) { this.showToast('Cannot delete task type', 'It may still be in use by a workflow.'); }
    },
    closeTaskTypeModal() {
      this.showTaskTypeModal = false;
      this.editingTaskType = null;
      this.taskTypeForm = { name: '', color: '#3B82F6', icon: 'tasks' };
    },

    // Action CRUD
    editAction(action) {
      this.editingAction = action;
      this.actionForm = {
        name: action.name,
        description: action.description,
        task_instruction_type: action.task_instruction_type,
        active: action.active !== false,
        config_json: action.config && Object.keys(action.config).length ? JSON.stringify(action.config, null, 2) : '{}',
      };
      this.showActionModal = true;
    },
    async saveAction() {
      if (this.configJsonError) { this.showToast('Invalid JSON', 'Fix the config JSON before saving.'); return; }
      try {
        let config = {};
        try { config = JSON.parse(this.actionForm.config_json || '{}'); } catch { config = {}; }
        const data = {
          name: this.actionForm.name,
          description: this.actionForm.description,
          task_instruction_type: this.actionForm.task_instruction_type,
          active: this.actionForm.active,
          task_type: this.selectedTaskType.id,
          config,
        };
        if (this.editingAction) {
          const shared = this.sharedMetricsFor(this.selectedTaskType.id);
          if (shared.length > 1) {
            const ok = await this.showConfirm({
              title: 'Shared task type',
              message: `This action belongs to a task type shared across ${shared.length} programs. Editing it will change all of them.`,
              programs: shared,
            });
            if (!ok) return;
          }
          await axios.patch(`/api/taskactiontypes/${this.editingAction.id}/`, data);
        } else {
          await axios.post('/api/taskactiontypes/', data);
        }
        this.closeActionModal();
        this.selectTaskType(this.selectedTaskType);
      } catch (e) {
        const configErrors = e.response?.data?.config;
        if (configErrors) {
          this.showToast('Config validation failed', Array.isArray(configErrors) ? configErrors.join(', ') : configErrors);
        } else {
          console.error(e);
          this.showToast('Error saving action', e.response?.data?.detail);
        }
      }
    },
    async toggleActionStatus(action) {
      const shared = this.sharedMetricsFor(this.selectedTaskType.id);
      if (shared.length > 1) {
        const ok = await this.showConfirm({
          title: 'Shared task type',
          message: `This action belongs to a task type shared across ${shared.length} programs. Toggling it will change all of them.`,
          programs: shared,
        });
        if (!ok) return;
      }
      try {
        await axios.patch(`/api/taskactiontypes/${action.id}/`, { active: !action.active });
        this.selectTaskType(this.selectedTaskType);
      } catch (e) { console.error(e); }
    },
    closeActionModal() {
      this.showActionModal = false;
      this.editingAction = null;
      this.actionForm = { name: '', description: '', task_instruction_type: null, active: true, config_json: '{}' };
    },

    // Work Type CRUD
    async assignWorkTypes() {
      this.loadingWorkTypes = true;
      try {
        await axios.post('/api/taskworktypes/bulk-assign/', {
          task_type_id: this.selectedTaskType.id,
          work_type_ids: this.selectedWorkTypeIds,
        });
        await this.fetchTaskTypes();
        this.selectedTaskType = this.taskTypes.find(tt => tt.id === this.selectedTaskType.id);
        this.closeWorkTypeAssignModal();
      } catch (e) { console.error(e); this.showToast('Error assigning work types', e.response?.data?.detail); }
      finally { this.loadingWorkTypes = false; }
    },
    async removeWorkType(wt) {
      const ok = await this.showConfirm({
        title: 'Remove work type',
        message: `Remove "${wt.name}" from this task type?`,
        variant: 'danger',
        confirmLabel: 'Remove',
      });
      if (!ok) return;
      try {
        await axios.delete(`/api/taskworktypes/${wt.task_work_type_id}/`);
        await this.fetchTaskTypes();
        this.selectedTaskType = this.taskTypes.find(tt => tt.id === this.selectedTaskType.id);
      } catch (e) { console.error(e); }
    },
    closeWorkTypeAssignModal() {
      this.showWorkTypeAssignModal = false;
      this.selectedWorkTypeIds = [];
    },
  },
  mounted() {
    this.fetchTaskTypes();
    this.fetchInstructionTypes();
    this.fetchAllWorkTypes();
    this.fetchMetrics();
    this.fetchMetricTaskTypes();
  },
};
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }

.shared-badge .shared-tooltip {
  display: none;
  position: absolute;
  left: 50%;
  bottom: calc(100% + 6px);
  transform: translateX(-50%);
  width: max-content;
  max-width: 260px;
  padding: 8px 10px;
  font-size: 11px;
  line-height: 1.4;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
  text-align: left;
  white-space: normal;
}
.shared-badge .shared-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #fde68a;
}
.shared-badge:hover .shared-tooltip { display: block; }
</style>
